import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Instagram, Facebook, MapPin, ArrowUpRight } from 'lucide-react';
import { LogoLockup } from './Logo';

const Footer = () => {
  const { language, t, switchTo } = useLanguage();

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/services', label: t.nav.services },
    { path: '/team', label: t.nav.team },
    { path: '/gallery', label: t.nav.gallery },
    { path: '/cercle', label: t.nav.clientele },
    { path: '/booking', label: t.nav.booking },
  ];

  return (
    <footer className="footer-editorial" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 pb-16 border-b border-white/10">
          {/* Logo & Tagline */}
          <div className="lg:col-span-5">
            {/* Forest Green Logo on Forest Green footer - use Noir variant for contrast */}
            <div className="mb-8">
              <LogoLockup variant="noir" size="md" />
            </div>
            <p className="font-body font-light text-sm text-white/60 leading-relaxed max-w-sm mb-8" style={{ lineHeight: 1.85 }}>
              {t.footer.tagline}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/casanova.mtl/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 eyebrow text-white/50 hover:text-[#F5F0E8] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram size={16} />
                <span className="hidden sm:inline">Instagram</span>
                <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                href="https://www.facebook.com/casanovasalonmtl/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 eyebrow text-white/50 hover:text-[#F5F0E8] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook size={16} />
                <span className="hidden sm:inline">Facebook</span>
                <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="lg:col-span-3">
            <h4 className="eyebrow text-white/40 mb-6">Navigation</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="font-body font-light text-sm text-white/70 hover:text-[#F5F0E8] transition-colors link-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h4 className="eyebrow text-white/40 mb-6 flex items-center gap-2">
              <MapPin size={14} />
              {t.booking.addressTitle}
            </h4>
            <address className="not-italic font-body font-light text-sm text-white/70 leading-relaxed mb-6">
              56 rue Jean-Talon Ouest<br />
              Montréal, QC H2R 2W7<br />
              <span className="text-[#A14223]">Petite-Italie</span>
            </address>

            {/* Hours Compact */}
            <div className="font-body font-light text-sm text-white/50">
              <span className="text-white/40">{t.booking.hours.monday} – {t.booking.hours.wednesday}:</span> 10h – 19h<br />
              <span className="text-white/40">{t.booking.hours.thursday} – {t.booking.hours.friday}:</span> 10h – 20h<br />
              <span className="text-white/40">{t.booking.hours.saturday} – {t.booking.hours.sunday}:</span> 9h – 16h
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="eyebrow text-white/40">{t.footer.copyright}</p>
          
          {/* Language Toggle */}
          <div className="lang-toggle lang-toggle-dark">
            <button
              onClick={() => switchTo('fr')}
              className={language === 'fr' ? 'active' : ''}
            >
              FR
            </button>
            <span className="text-white/30 mx-1">/</span>
            <button
              onClick={() => switchTo('en')}
              className={language === 'en' ? 'active' : ''}
            >
              EN
            </button>
          </div>
          
          <span className="est-badge border-white/20 text-white/40">Est. 2020</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
